import { useState } from 'react';
import './Slider.scss';
import { useSessionStorage } from 'Utilities/UseStorage';
import { useEffect } from 'react';
import { IIcon, ISliderSettings } from './Models/Panels';
import { Panel } from './Panel';
import { GetSlider } from './SliderApi';
import { triggerGoal } from 'Utilities/GoalManager';
import {
  IconPinUnfilled,
  IconPinFilled,
  IconCollapseLeft,
  IconSeeMore,
  IconCollapseRight,
} from '@lmig/lmds-react-icons';
import { SliderTile } from './Models/Tiles';
import { getWindowWidth, isMobileView, isTabletView } from 'Utilities/AppHelper';

interface ISliderTabProps {
  title: string;
  icon: IIcon;
  open: () => void;
  isOpen: boolean;
  badges?: IBadge[];
  goal: string;
}

const SliderTab = (props: ISliderTabProps) => {
  const [icon, setIcon] = useState<string>();
  const badge = props.badges?.find((badge) => props.title === badge.title);
  const openSlider = () => {
    props.open();
    if (props.goal.length > 0) triggerGoal(props.goal);
  };

  useEffect(() => {
    if (props?.icon?.src && props?.icon?.src?.length !== 0) {
      fetch(props.icon.src)
        .then((x) => x.text())
        .then((x: string) => {
          if (x) setIcon(x);
        });
    }
  }, [props.icon.src]);

  return (
    <div
      className={`slider-panel-tab ${props.isOpen ? 'active-tab' : ''} ${badge?.showBadge ? 'new-content' : ''}`}
      onClick={() => openSlider()}
    >
      <div className="slider-panel-badge">
        {icon && <span dangerouslySetInnerHTML={{ __html: icon }}></span>}
        <span className="slider-panel-text">{props.title}</span>
      </div>
    </div>
  );
};
interface IBadge {
  title?: string;
  showBadge?: boolean;
}
const Slider = () => {
  /* lmds slider uses fixed positioning and is designed to overlay content instead of pushing it so were not using it */
  const [panels, setPanels] = useState<SliderTile[]>([]);
  const expirationMinutes: number = 8 * 60;
  const [settings, setSettings] = useSessionStorage<ISliderSettings>('slider_settings', undefined, expirationMinutes);
  const [useTransition, setUseTransition] = useState<boolean>(true);
  const [showBadge, setShowBadge] = useState<IBadge[]>([]);
  const [isToggling, setIsToggling] = useState<boolean>(false);
  const [renderControls, setRenderControls] = useState(1);
  const [moreTrayColumns, setMoreTrayColumns] = useState<number>(5);
  const [showMoreButton, setShowMoreButton] = useState<boolean>(false);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState<boolean>(false);
  const [showHorizontalMenu, setShowHorizontalMenu] = useState<boolean>(false);
  const [moreTrayOpen, setMoreTrayOpen] = useState<boolean>(false);

  useEffect(() => {
    function moreTrayOnScreenResize() {
      setIsMobileOrTablet(isMobileView() || isTabletView());
      setShowHorizontalMenu(getWindowWidth() < 1081);
      setMoreTrayColumns(isMobileView() ? 5 : 7);
      setShowMoreButton(() => {
        if (isMobileView() && panels.length > 5) return true;
        else if (isTabletView() && panels.length > 7) return true;
        else return false;
      });
    }
    moreTrayOnScreenResize();
    window.addEventListener('resize', moreTrayOnScreenResize);
  }, [panels]);

  const showMore = () => {
    setMoreTrayOpen((moreTrayOpen) => !moreTrayOpen);
  };

  useEffect(() => {
    const getSliderAsync = async () => {
      const sliderPanels: SliderTile[] = await GetSlider();
      if (sliderPanels && sliderPanels.length) {
        setPanels(sliderPanels.sort((a, b) => parseInt(a.sortorder) - parseInt(b.sortorder))); //sort panels by sortorder
      }
    };
    getSliderAsync();
  }, []);

  useEffect(() => {
    if (!isToggling) {
      let path = window.location.pathname;
      if (
        path === '/start' ||
        path === '/start_v2' ||
        path === '/start/' ||
        path === '/start_v2/' ||
        path === '/start_classic' ||
        path === '/start_classic/'
      ) {
        //only start page loads as open
        if (!settings && panels.length > 0) {
          setSettings({
            data: {
              activeTab: panels[0].title,
              isOpen: isSmallerThanDesktopView() ? false : true,
              pinned: false,
            },
          });
        } else if (settings) {
          setSettings({
            data: {
              activeTab: settings.activeTab,
              isOpen: isSmallerThanDesktopView() ? false : true,
              pinned: isSmallerThanDesktopView() ? false : settings.pinned,
            },
          });
        }
      } else {
        //every other page loads unpinned and closed.
        if (!settings && panels.length > 0) {
          setSettings({ data: { activeTab: panels[0].title, isOpen: false, pinned: false } });
        } else if (settings) {
          setSettings({
            data: {
              activeTab: settings.activeTab,
              isOpen: settings.pinned,
              pinned: settings.pinned,
            },
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panels]);

  //Toggling logic on panel tab click
  const [elementFromMoreActive, setElementFromMoreActive] = useState<boolean>(false);
  const toggle = (title: string) => {
    setRenderControls((v) => v + 1);
    setIsToggling(true);
    setMoreTrayOpen(false);
    if (settings?.isOpen && title === settings?.activeTab) {
      //closing a tab by clicking on it
      if (isMobileOrTablet) {
        document.getElementsByTagName('html')[0].style.overflow = 'auto';
      }
      setUseTransition(true);
      setSettings({ data: { ...settings, isOpen: false } });
    } else if (settings?.isOpen) {
      //going from one open tab to another
      setUseTransition(false);
      setSettings({ data: { ...settings, isOpen: true, activeTab: title } });
    } else {
      if (isMobileOrTablet) {
        document.getElementsByTagName('html')[0].style.overflow = 'hidden';
      }
      setSettings({ data: { pinned: settings?.pinned ?? false, isOpen: true, activeTab: title } });
      //opening a tab when slider is collapsed
    }
  };

  useEffect(() => {
    if (
      moreTrayOpen ||
      (settings?.isOpen &&
        panels
          .slice(moreTrayColumns - 1)
          .map((panel) => panel.title)
          .includes(settings.activeTab || ''))
    ) {
      setElementFromMoreActive(true);
    } else {
      setElementFromMoreActive(false);
    }
  }, [settings, settings, moreTrayOpen, panels]);

  const showBadgeUpdate = (title: string, show: boolean) => {
    setShowBadge((prev) => [...prev.filter((x) => x.title !== title), { title: title, showBadge: show }]);
  };

  const isSmallerThanDesktopView = () => {
    return window.innerWidth < 1180;
  };

  return (
    <div className={`slider-wrapper ${settings?.isOpen ? 'open' : 'closed'}`}>
      {settings?.isOpen ? (
        <div
          className="hide-menu desktop-header"
          onClick={() => {
            setIsToggling(true);
            setSettings({ data: { activeTab: settings?.activeTab, isOpen: false, pinned: settings?.pinned } });
          }}
        >
          <IconCollapseLeft size="24" color="teal" />
          <p> Hide menu</p>
        </div>
      ) : (
        <div
          className="open-menu desktop-header"
          onClick={() => {
            setIsToggling(false);
            setSettings({ data: { activeTab: settings?.activeTab, isOpen: true, pinned: settings?.pinned } });
          }}
        >
          <IconCollapseRight size="24" color="teal" />
          <p> Open menu</p>
        </div>
      )}
      {settings && (
        <div className={`slider ${settings.isOpen ? (settings.pinned ? 'pinned' : 'open') : 'closed'}`}>
          {moreTrayOpen && showMoreButton ? (
            <div id="mobile-slider-tabs-tray">
              <div id="mobile-slider-tabs-tray-overlay"></div>
              <div
                id="mobile-slider-tabs-tray-content"
                style={{ gridTemplateColumns: `repeat(${moreTrayColumns}, minmax(0, 1fr))` }}
              >
                {panels.slice(moreTrayColumns - 1).map((panel) => (
                  <SliderTab
                    key={panel.title}
                    title={panel.title}
                    icon={panel.icon ?? {}}
                    open={() => toggle(panel.title)}
                    isOpen={(settings?.isOpen && settings?.activeTab === panel.title) ?? false}
                    badges={showBadge}
                    goal={panel.goal}
                  />
                ))}
              </div>
            </div>
          ) : null}
          <div className={`slider-tabs ${settings?.isOpen ? 'open' : 'closed'}`}>
            {(showMoreButton ? panels.slice(0, moreTrayColumns - 1) : panels).map((panel: SliderTile) => (
              <SliderTab
                key={panel.title}
                title={panel.title}
                icon={panel.icon ?? {}}
                open={() => toggle(panel.title)}
                isOpen={(settings?.isOpen && settings?.activeTab === panel.title) ?? false}
                badges={showBadge}
                goal={panel.goal}
              />
            ))}
            {showMoreButton ? (
              <div
                id="slider-show-more-button"
                className={`slider-panel-tab ${elementFromMoreActive ? 'more-button active' : 'more-button'}`}
                onClick={showMore}
              >
                <div className="slider-panel-badge">
                  <span>
                    <IconSeeMore size="16" />
                  </span>
                  <span className="slider-panel-text">More</span>
                </div>
              </div>
            ) : null}
          </div>
          <div
            className={`slider-panels d-flex ${useTransition ? '' : 'no-transition'} ${
              settings.pinned ? 'pinned' : ''
            }`}
          >
            <div className="slider-controls" key={renderControls}>
              {settings.pinned ? (
                <div className="icon-circle">
                  <IconPinFilled
                    size="16"
                    className="slider-pin"
                    onClick={() => {
                      setIsToggling(true);
                      setSettings({ data: { activeTab: settings?.activeTab, isOpen: true, pinned: false } });
                    }}
                  />
                </div>
              ) : (
                <div className="icon-circle">
                  <IconPinUnfilled
                    size="16"
                    className="slider-pin"
                    onClick={() => {
                      setIsToggling(true);
                      setSettings({ data: { activeTab: settings?.activeTab, isOpen: true, pinned: true } });
                    }}
                  />
                </div>
              )}
            </div>
            {panels.map((panel: SliderTile, i) => (
              <div key={i}>
                {settings && (
                  <Panel
                    panel={panel}
                    sliderSettings={settings}
                    showBadge={showBadgeUpdate}
                    close={() => toggle(panel.title)}
                    showHorizontalMenu={showHorizontalMenu}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
export default Slider;
